/**
 * Setting a destination and, straight away, offering to plot a route to it.
 *
 * Reported by a training user: pressing "Set destination" only dropped the
 * destination flag, so the obvious next step (a route to it) was a separate hunt.
 * Now we set the destination and immediately offer two ways to plot it:
 *
 *   Manual — a single straight leg (rhumb line) from the boat to the point, which
 *     we then shape by long-pressing the line to drop waypoints around hazards.
 *   Auto  — the chart-aware A* router lays a route that keeps to navigable water
 *     for our draught, using the tide (and wind, with a polar) at each step. The
 *     map runs it against the loaded chart cells; on a chart with no charted
 *     depths it reports why rather than plotting a route it can't stand behind.
 *
 * It's a quick choice, not a mode switch, so dismissing leaves us exactly where we
 * were with just the destination set. With no fix yet there's no start to plot
 * from, so we only set the destination and skip the offer.
 */
import { useShellStore } from '@/store/shell';
import { useRoutingStore, nextWaypointId, nextRouteIdFn } from '@/store/routing';
import { useChartPlotterStore } from '@/store';
import { useRoutePlanStore, buildPassageRoute } from '@/store/routePlan';

export function setDestinationAndOfferRoute(position: [number, number], name?: string): void {
  useRoutingStore.getState().setDestination(position, name);
  const shell = useShellStore.getState();
  shell.showToast('Destination set');

  const boat = useChartPlotterStore.getState().ownship.position;
  if (!boat) return;

  shell.showConfirm({
    message: 'Plot a route to your destination? Manual draws a straight line to shape yourself; Auto keeps to navigable water for you.',
    confirmLabel: 'Manual',
    onConfirm: () => {
      const id = nextRouteIdFn();
      const route = buildPassageRoute({
        id,
        start: boat,
        destination: position,
        destName: name,
        makeWpId: nextWaypointId,
      });
      const rs = useRoutingStore.getState();
      rs.upsertRoute(route);
      rs.setActiveRoute(id);
      useShellStore.getState().showToast('Route plotted — long-press the line to shape it');
    },
    altLabel: 'Auto',
    onAlt: () => {
      // Hand the leg-planning to the chart-aware router. The map owns the chart
      // cells, so it picks this request up, runs it, and upserts the route (or
      // toasts why it couldn't). We just fire the request and say it's working.
      useRoutePlanStore.getState().requestAutoRoute(boat, position);
      useShellStore.getState().showToast('Auto-routing — finding a route that keeps to navigable water');
    },
  });
}
